import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { DialogContent, DialogActions } from '@mui/material';
import { useMembers } from '@app/api/members/useMembers';
import { ThemeButton } from '@shared/ui/buttons';
import Note from '@shared/ui/messages/Note';
import {
  MemberRoleView,
  MemberNewRoleView,
  BasicSelectInput,
  CardsActionPicker,
} from '@shared/ui/forms/inputs';
import { icons } from '@shared/static/icons';
import { roles } from '@shared/static/roles';
import { getTotalCards } from '@utils/helpers';
import { validateServerErrors } from '@utils/validation';

export default function ChangeRoleDialog({ close, data, alreadyHasTeamLead }) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { cardsAction: 'keep', group_id: data.groups?.[0]?.id },
  });

  const { leadToMember, memberToLead } = useMembers();
  const { allGroups } = useSelector((state) => state.my_team);
  const [loading, setLoading] = useState(false);

  const cardsActionWatch = useWatch({ control, name: 'cardsAction' });

  const isLead = data.group_role === roles.TEAM_LEAD;

  const cardOwners =
    allGroups
      ?.find((group) => group.id === data.groups?.[0]?.id)
      ?.users?.filter((user) => user.id !== data.id) || [];

  async function onSubmit(formData) {
    setLoading(true);

    const payload = isLead
      ? { user_id: data.id, group_id: formData.group_id }
      : {
          user_id: data.id,
          group_ids: [formData.group_id],
          ...(formData.cardsAction === 'move' && {
            new_card_owner_id: formData.new_card_owner_id,
          }),
        };

    try {
      const res = await (isLead ? leadToMember : memberToLead)(payload);
      if (res) close();
    } catch (error) {
      validateServerErrors(setError, error);
    } finally {
      setLoading(false);
    }
  }

  return alreadyHasTeamLead ? (
    <DialogContent>
      <MemberRoleView member={data} />
      <Note
        type="error"
        title={t('change_role_error_note_title')}
        marginTop
        content={t('change_role_error_note_text')}
      />
    </DialogContent>
  ) : (
    <>
      <DialogContent>
        <Note
          type="warning"
          title={t('important')}
          marginBottom
          content={t(
            isLead
              ? 'change_member_role_note_lead'
              : 'change_member_role_note_member',
          )}
        />
        <div className="form">
          <div className="form-group-arrow">
            <MemberRoleView
              member={data}
              label={t('member')}
              error={errors.user_id}
            />
            <div className="arrow-icon mt">{icons['arrow-right']}</div>
            <MemberNewRoleView
              label={t('new_role')}
              name="group_id"
              member={data}
              control={control}
              error={errors.group_id}
            />
          </div>
          {getTotalCards(data) > 0 && !isLead && (
            <div className="form-group-arrow">
              <CardsActionPicker
                type="changeRole"
                control={control}
                name="cardsAction"
                error={errors.cardsAction}
              />
              <div
                className={`arrow-icon ${
                  cardsActionWatch !== 'move' ? 'hidden' : ''
                }`}
              >
                {icons['arrow-right']}
              </div>
              {cardsActionWatch === 'move' && (
                <BasicSelectInput
                  control={control}
                  name="new_card_owner_id"
                  label={t('new_owner')}
                  icon="user-input"
                  placeholder={t('Administrator')}
                  searchable
                  error={errors.new_card_owner_id}
                  data={cardOwners}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          startIcon="check"
          block={false}
          loading={loading}
          minWidth
          onClick={handleSubmit(onSubmit)}
        >
          {t('save')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
